import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import { BarChart, TrendingUp, DollarSign, Package, Clock, MapPin, Activity, TrendingDown, Menu } from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import SideNavigation from '../components/SideNavigation';
import useScreenSize from '../hooks/useScreenSize';
import { useDispatch } from 'react-redux';
import { fetchNotifications } from '../store/slices/notificationSlice';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Enhanced 3D Bar Component with hover effects and animations
const Bar3D = ({ position, height, color, data }) => {
  const { isMobile } = useScreenSize();
  const [hovered, setHovered] = useState(false);
  
  if (!data) return null; // Add safety check
  
  return (
    <group position={position}>
      <mesh
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        scale={hovered && !isMobile ? [1.1, 1.1, 1.1] : [1, 1, 1]}
      >
        <boxGeometry args={[0.8, height, 0.8]} />
        <meshStandardMaterial
          color={hovered ? '#6366f1' : color}
          metalness={0.8}
          roughness={0.2}
          emissive={hovered ? color : 'black'}
          emissiveIntensity={hovered ? 0.5 : 0}
        />
      </mesh>
      
      {/* Volume Label - hide on mobile */}
      {data.volume && !isMobile && (
        <Html position={[0, height + 0.5, 0]} center>
          <div className={`px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 ${
            hovered ? 'bg-indigo-600 text-white scale-110' : 'bg-white/90 text-gray-800'
          }`}>
            {data.volume} tons
          </div>
        </Html>
      )}
      
      {/* Date Label - smaller on mobile */}
      {data.date && (
        <Html position={[0, -0.5, 0]} center>
          <div className="text-[10px] sm:text-xs text-gray-600">
            {data.date}
          </div>
        </Html>
      )}
    </group>
  );
};

// Enhanced 3D Graph Component with multiple metrics
const Graph3D = ({ data }) => {
  const { isMobile } = useScreenSize();
  
  const bars = useMemo(() => {
    if (!Array.isArray(data)) return [];
    
    return data.map((item, index) => {
      if (!item || typeof item.volume === 'undefined') return null;
      
      const height = item.volume / 20; // Scale down the height
      const spacing = isMobile ? 0.8 : 1.2; // Reduce spacing on mobile
      const position = [(index - data.length / 2) * spacing, height / 2, 0];
      const color = `hsl(${220 + (index * 20)}, 70%, ${item.efficiency}%)`;
      
      return (
        <Bar3D
          key={index}
          position={position}
          height={height}
          color={color}
          data={item}
        />
      );
    }).filter(Boolean); // Remove any null elements
  }, [data, isMobile]);

  return (
    <div className="relative h-full">
      <Canvas camera={{ position: [0, 4, isMobile ? 12 : 8], fov: 45 }}>
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <spotLight position={[-10, 10, -10]} angle={0.3} />
        
        {/* Add fog for depth perception */}
        <fog attach="fog" args={['#f3f4f6', 8, 25]} />
        
        {/* Grid helper */}
        <gridHelper args={[20, 20, '#cbd5e1', '#cbd5e1']} position={[0, -0.01, 0]} />
        
        <group>
          {bars}
        </group>
        
        <OrbitControls
          enableZoom={true}
          maxPolarAngle={Math.PI / 2}
          minPolarAngle={0}
        />
      </Canvas>
      
      {/* Legend - make responsive */}
      <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-white/90 p-2 sm:p-3 rounded-lg shadow-lg text-[10px] sm:text-sm">
        <div className="text-sm font-medium mb-2">Color indicates efficiency:</div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-red-500"></div>
          <span className="text-xs">Low</span>
          <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
          <span className="text-xs">Medium</span>
          <div className="w-4 h-4 rounded-full bg-green-500"></div>
          <span className="text-xs">High</span>
        </div>
      </div>
    </div>
  );
};

const Analytics = () => {
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  // Mock data
  const monthlyRevenue = useMemo(() => ({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [{
      label: 'Revenue',
      data: [65000, 72000, 68000, 85000, 82000, 90000, 88000, 95000, 98000, 102000, 105000, 110000],
      fill: true,
      borderColor: 'rgb(99, 102, 241)',
      backgroundColor: 'rgba(99, 102, 241, 0.1)',
      tension: 0.4
    }]
  }), []);

  const deliveryPerformance = useMemo(() => ({
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{
      label: 'On-Time Deliveries',
      data: [92, 95, 89, 96, 94, 91, 93],
      backgroundColor: 'rgba(147, 51, 234, 0.8)',
      borderRadius: 8,
    }]
  }), []);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#6B7280',
          font: {
            family: 'Inter, sans-serif',
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(107, 114, 128, 0.1)',
        },
        ticks: {
          color: '#6B7280',
          font: {
            family: 'Inter, sans-serif',
          }
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            family: 'Inter, sans-serif',
          }
        }
      }
    }
  };

  // Enhanced mock data for 3D visualization
  const volumeData = useMemo(() => [
    { date: 'Jan 1', volume: 45, efficiency: 65, routes: 12 },
    { date: 'Jan 5', volume: 62, efficiency: 78, routes: 15 },
    { date: 'Jan 10', volume: 58, efficiency: 82, routes: 14 },
    { date: 'Jan 15', volume: 71, efficiency: 88, routes: 18 },
    { date: 'Jan 20', volume: 68, efficiency: 75, routes: 16 },
    { date: 'Jan 25', volume: 75, efficiency: 92, routes: 20 },
    { date: 'Feb 1', volume: 82, efficiency: 85, routes: 22 },
    { date: 'Feb 5', volume: 88, efficiency: 90, routes: 24 },
    { date: 'Feb 10', volume: 85, efficiency: 87, routes: 23 },
    { date: 'Feb 15', volume: 92, efficiency: 95, routes: 25 }
  ], []);

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  return (
    <div className="bg-gray-100 min-h-screen flex">
      <SideNavigation 
        isOpen={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      <main className={`flex-1 overflow-x-hidden overflow-y-auto transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0 px-2' : (sidebarOpen ? 'ml-64' : 'ml-16')
      }`}>
        <div className="max-w-7xl mx-auto py-4 sm:py-6 px-2 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4 px-2">
            {(isMobile || isTablet) && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 bg-white rounded-md shadow-md"
              >
                <Menu className="h-6 w-6" />
              </button>
            )}
            <motion.div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 ml-2">
                Analytics Dashboard
              </h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 ml-2">
                Comprehensive insights into your logistics operations
              </p>
            </motion.div>
          </div>

          {/* Data requirement notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 sm:mb-8">
            <p className="text-sm text-blue-700">
              Note: Complete 100 loads to view your actual analytics data. Currently displaying sample data for preview purposes.
            </p>
          </div>

          {/* Key Metrics Grid - adjust for mobile */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
            {[
              { icon: DollarSign, label: 'Revenue', value: '$110,000', trend: '+12%', trendUp: true },
              { icon: Package, label: 'Deliveries', value: '1,234', trend: '+8%', trendUp: true },
              { icon: Clock, label: 'Avg. Delivery Time', value: '2.3 days', trend: '-5%', trendUp: false },
              { icon: Activity, label: 'Success Rate', value: '98.5%', trend: '+2%', trendUp: true },
            ].map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="p-3 rounded-lg bg-indigo-100">
                    <metric.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className={`flex items-center ${metric.trendUp ? 'text-green-500' : 'text-red-500'}`}>
                    {metric.trendUp ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                    <span className="text-sm font-medium">{metric.trend}</span>
                  </div>
                </div>
                <h3 className="mt-4 text-2xl font-bold text-gray-900">{metric.value}</h3>
                <p className="text-gray-600">{metric.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Charts Grid - stack on mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-8">
            {/* Revenue Chart */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Monthly Revenue</h3>
              <Line data={monthlyRevenue} options={chartOptions} />
            </motion.div>

            {/* Delivery Performance Chart */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Weekly Delivery Performance</h3>
              <Bar data={deliveryPerformance} options={chartOptions} />
            </motion.div>
          </div>

          {/* 3D Visualization Section */}
          <motion.div className="bg-white rounded-xl shadow-lg p-3 sm:p-6 mb-4 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6">
              <div className="mb-3 sm:mb-0">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Shipping Volume & Efficiency</h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  3D visualization of shipping volumes
                </p>
              </div>
              <select className="text-xs sm:text-sm border rounded-lg px-2 py-1 sm:px-3 sm:py-2">
                <option>Last 30 Days</option>
                <option>Last 90 Days</option>
                <option>Last Year</option>
              </select>
            </div>
            
            <div className="h-[300px] sm:h-[500px]">
              <Graph3D data={volumeData} />
            </div>
            
            {/* Metrics summary - adjust for mobile */}
            <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-2 sm:gap-4 text-center">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm text-gray-600">Average Volume</div>
                <div className="text-lg font-bold text-gray-900">72.6 tons</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm text-gray-600">Efficiency Rate</div>
                <div className="text-lg font-bold text-gray-900">83.7%</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm text-gray-600">Total Routes</div>
                <div className="text-lg font-bold text-gray-900">189</div>
              </div>
            </div>
          </motion.div>

          {/* Additional Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Add more detailed metric cards here */}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Analytics; 